import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { appointmentsAPI, clientAPI } from '../services/api';
import {
  Calendar as CalendarIcon,
  Plus,
  Clock,
  MapPin,
  User,
  Phone,
  Mail,
  CheckCircle,
  XCircle,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Download,
  Search,
  X
} from 'lucide-react';

const AppointmentsPage = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendar'); // 'list' or 'calendar'
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [clientSearch, setClientSearch] = useState('');
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayModal, setShowDayModal] = useState(false);

  const [newAppointment, setNewAppointment] = useState({
    title: '',
    appointment_type: 'Viewing',
    client_id: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    start_time: '',
    end_time: '',
    location: '',
    address: '',
    description: '',
    notes: '',
    status: 'Scheduled'
  });

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [appointmentsRes, clientsRes] = await Promise.all([
        appointmentsAPI.getAll(),
        clientAPI.getAll({ page: 1, per_page: 1000 })
      ]);
      setAppointments(appointmentsRes.data);
      setClients(clientsRes.data.clients || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      // Convert datetime-local format to ISO 8601 with timezone
      const appointmentData = {
        ...newAppointment,
        start_time: newAppointment.start_time ? new Date(newAppointment.start_time).toISOString() : null,
        end_time: newAppointment.end_time ? new Date(newAppointment.end_time).toISOString() : null,
        // Remove empty string values
        client_id: newAppointment.client_id || null,
      };

      await appointmentsAPI.create(appointmentData);
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      console.error('Error creating appointment:', err);
      setError(err.response?.data?.detail || 'Failed to create appointment');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this appointment?')) {
      try {
        setError('');
        await appointmentsAPI.delete(id);
        fetchData();
      } catch (err) {
        console.error('Error deleting appointment:', err);
        setError('Failed to delete appointment');
      }
    }
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      setError('');
      await appointmentsAPI.update(id, { status: newStatus });
      fetchData();
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status');
    }
  };

  const resetForm = () => {
    setNewAppointment({
      title: '',
      appointment_type: 'Viewing',
      client_id: '',
      client_name: '',
      client_email: '',
      client_phone: '',
      start_time: '',
      end_time: '',
      location: '',
      address: '',
      description: '',
      notes: '',
      status: 'Scheduled'
    });
    setClientSearch('');
    setShowClientSuggestions(false);
  };

  const handleClientSelect = (client) => {
    setNewAppointment({
      ...newAppointment,
      client_id: client.id,
      client_name: client.name,
      client_email: client.email || '',
      client_phone: client.phone || ''
    });
    setClientSearch(client.name);
    setShowClientSuggestions(false);
  };

  const handleClientSearchChange = (value) => {
    setClientSearch(value);
    setShowClientSuggestions(value.length > 0);

    // If search is cleared, clear the selected client
    if (value.length === 0) {
      setNewAppointment({
        ...newAppointment,
        client_id: '',
        client_name: '',
        client_email: '',
        client_phone: ''
      });
    }
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    (client.email && client.email.toLowerCase().includes(clientSearch.toLowerCase())) ||
    (client.company && client.company.toLowerCase().includes(clientSearch.toLowerCase()))
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled':
        return 'bg-blue-100 text-blue-700';
      case 'Confirmed':
        return 'bg-green-100 text-green-700';
      case 'Completed':
        return 'bg-gray-100 text-gray-700';
      case 'Cancelled':
        return 'bg-red-100 text-red-700';
      case 'No Show':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Viewing':
        return 'bg-purple-100 text-purple-700';
      case 'Service':
        return 'bg-blue-100 text-blue-700';
      case 'Consultation':
        return 'bg-amber-100 text-amber-700';
      case 'Pickup':
        return 'bg-green-100 text-green-700';
      case 'Delivery':
        return 'bg-emerald-100 text-emerald-700';
      case 'Appraisal':
        return 'bg-rose-100 text-rose-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatICSDate = (dateString) => {
    // Convert to UTC format: YYYYMMDDTHHMMSSZ
    const date = new Date(dateString);
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  };

  const downloadCalendarInvite = (appointment) => {
    // Generate .ics file content
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Dial-Grid//Watch Dealer CRM//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:appointment-${appointment.id}@dial-grid.com`,
      `DTSTAMP:${formatICSDate(new Date().toISOString())}`,
      `DTSTART:${formatICSDate(appointment.start_time)}`,
      `DTEND:${formatICSDate(appointment.end_time)}`,
      `SUMMARY:${appointment.title}`,
      `DESCRIPTION:${appointment.description || ''} - Type: ${appointment.appointment_type}`,
      appointment.location ? `LOCATION:${appointment.location}${appointment.address ? ', ' + appointment.address : ''}` : '',
      `STATUS:${appointment.status.toUpperCase().replace(' ', '-')}`,
      appointment.client_name ? `ORGANIZER;CN=${appointment.client_name}:mailto:${appointment.client_email || 'noreply@chronosinventory.com'}` : '',
      'END:VEVENT',
      'END:VCALENDAR'
    ].filter(line => line !== '').join('\r\n');

    // Create blob and download
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `appointment-${appointment.id}-${appointment.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const upcomingAppointments = appointments
    .filter(apt => new Date(apt.start_time) > new Date())
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

  const pastAppointments = appointments
    .filter(apt => new Date(apt.start_time) <= new Date())
    .sort((a, b) => new Date(b.start_time) - new Date(a.start_time));

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  };

  const getAppointmentsForDay = (day) => {
    if (!day) return [];
    return appointments.filter(apt => {
      const aptDate = new Date(apt.start_time);
      return isSameDay(aptDate, day);
    });
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-900 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-800 px-4 py-3 rounded-xl">
          <p className="font-medium">{error}</p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">Calendar & Appointments</h1>
          <p className="text-gray-600 mt-2">Schedule client viewings, service appointments, and consultations</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-xl">
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                viewMode === 'calendar'
                  ? 'bg-white text-slate-900 shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <CalendarIcon className="w-4 h-4 inline mr-2" />
              Calendar
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                viewMode === 'list'
                  ? 'bg-white text-slate-900 shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Clock className="w-4 h-4 inline mr-2" />
              List
            </button>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-2xl font-semibold shadow-lg hover:from-slate-950 hover:to-slate-800 transition-all"
          >
            <Plus className="w-5 h-5" />
            New Appointment
          </button>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {formatMonthYear(currentMonth)}
            </h2>
            <div className="flex items-center gap-3">
              <button
                onClick={goToToday}
                className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all"
              >
                Today
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={goToPreviousMonth}
                  className="p-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-all"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={goToNextMonth}
                  className="p-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-all"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2">
            {/* Day Headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="text-center font-bold text-gray-700 py-2 text-sm">
                {day}
              </div>
            ))}

            {/* Calendar Days */}
            {getDaysInMonth(currentMonth).map((day, index) => {
              const dayAppointments = getAppointmentsForDay(day);
              const isToday = day && isSameDay(day, new Date());

              return (
                <div
                  key={index}
                  onClick={() => {
                    if (day) {
                      setSelectedDay(day);
                      setShowDayModal(true);
                    }
                  }}
                  className={`min-h-[120px] border-2 rounded-xl p-2 transition-all ${
                    day
                      ? isToday
                        ? 'border-blue-500 bg-blue-50 cursor-pointer'
                        : 'border-gray-200 hover:border-slate-300 hover:shadow-md bg-white cursor-pointer'
                      : 'bg-gray-50 border-gray-100'
                  }`}
                >
                  {day && (
                    <>
                      <div className={`text-right font-bold mb-1 ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
                        {day.getDate()}
                      </div>
                      <div className="space-y-1">
                        {dayAppointments.slice(0, 3).map((apt) => (
                          <div
                            key={apt.id}
                            onClick={() => {
                              setSelectedAppointment(apt);
                              setShowDetailModal(true);
                            }}
                            className={`text-xs p-1.5 rounded-lg cursor-pointer transition-all ${getTypeColor(apt.appointment_type)} hover:shadow-md`}
                            title={`${apt.title} - ${new Date(apt.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
                          >
                            <div className="font-semibold truncate">{apt.title}</div>
                            <div className="text-[10px] truncate">
                              {new Date(apt.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                            </div>
                          </div>
                        ))}
                        {dayAppointments.length > 3 && (
                          <div className="text-[10px] text-center text-gray-600 font-semibold">
                            +{dayAppointments.length - 3} more
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* List View - Upcoming Appointments */}
      {viewMode === 'list' && (
        <>
      <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <Clock className="w-7 h-7 text-blue-600" />
          Upcoming Appointments ({upcomingAppointments.length})
        </h2>

        {upcomingAppointments.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <CalendarIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>No upcoming appointments</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 px-6 py-3 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-all"
            >
              Schedule First Appointment
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {upcomingAppointments.map((appointment) => (
              <div
                key={appointment.id}
                className="border-2 border-gray-200 rounded-2xl p-6 hover:border-blue-300 hover:shadow-lg transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{appointment.title}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${getTypeColor(appointment.appointment_type)}`}>
                        {appointment.appointment_type}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(appointment.status)}`}>
                        {appointment.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-blue-600 font-semibold">
                      <Clock className="w-4 h-4" />
                      {formatDateTime(appointment.start_time)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => downloadCalendarInvite(appointment)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Download Calendar Invite"
                    >
                      <Download className="w-5 h-5" />
                    </button>
                    {appointment.status === 'Scheduled' && (
                      <button
                        onClick={() => handleUpdateStatus(appointment.id, 'Confirmed')}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Confirm"
                      >
                        <CheckCircle className="w-5 h-5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(appointment.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  {(appointment.client_name || appointment.client_id) && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <User className="w-4 h-4" />
                      {appointment.client_name || `Client #${appointment.client_id}`}
                    </div>
                  )}
                  {appointment.client_phone && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Phone className="w-4 h-4" />
                      {appointment.client_phone}
                    </div>
                  )}
                  {appointment.location && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <MapPin className="w-4 h-4" />
                      {appointment.location}
                    </div>
                  )}
                </div>

                {appointment.description && (
                  <p className="mt-3 text-gray-600 text-sm">{appointment.description}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Past Appointments */}
      {pastAppointments.length > 0 && (
        <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Past Appointments ({pastAppointments.length})
          </h2>
          <div className="space-y-3">
            {pastAppointments.slice(0, 10).map((appointment) => (
              <div
                key={appointment.id}
                className="border border-gray-200 rounded-xl p-4 hover:bg-gray-50 transition-all opacity-75"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-bold text-gray-900">{appointment.title}</h4>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${getStatusColor(appointment.status)}`}>
                        {appointment.status}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {formatDateTime(appointment.start_time)} • {appointment.client_name || 'No client'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => downloadCalendarInvite(appointment)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Download Calendar Invite"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(appointment.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
        </>
      )}

      {/* Create Appointment Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">New Appointment</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  required
                  value={newAppointment.title}
                  onChange={(e) => setNewAppointment({ ...newAppointment, title: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-900 focus:outline-none"
                  placeholder="e.g., Rolex Submariner Viewing"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Type *</label>
                  <select
                    required
                    value={newAppointment.appointment_type}
                    onChange={(e) => setNewAppointment({ ...newAppointment, appointment_type: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-900 focus:outline-none"
                  >
                    <option value="Viewing">Viewing</option>
                    <option value="Service">Service</option>
                    <option value="Consultation">Consultation</option>
                    <option value="Pickup">Pickup</option>
                    <option value="Delivery">Delivery</option>
                    <option value="Appraisal">Appraisal</option>
                    <option value="Meeting">Meeting</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Client</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={clientSearch}
                      onChange={(e) => handleClientSearchChange(e.target.value)}
                      onFocus={() => setShowClientSuggestions(clientSearch.length > 0)}
                      placeholder="Search clients or enter manually..."
                      className="w-full pl-10 pr-10 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-900 focus:outline-none"
                    />
                    {clientSearch && (
                      <button
                        type="button"
                        onClick={() => {
                          setClientSearch('');
                          setShowClientSuggestions(false);
                          setNewAppointment({
                            ...newAppointment,
                            client_id: '',
                            client_name: '',
                            client_email: '',
                            client_phone: ''
                          });
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>

                  {/* Client Suggestions Dropdown */}
                  {showClientSuggestions && filteredClients.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {filteredClients.slice(0, 10).map((client) => (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => handleClientSelect(client)}
                          className="w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-semibold text-gray-900">{client.name}</div>
                          {client.email && (
                            <div className="text-sm text-gray-500">{client.email}</div>
                          )}
                          {client.company && (
                            <div className="text-xs text-gray-400">{client.company}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {newAppointment.client_id && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      <span>Client selected: {newAppointment.client_name}</span>
                    </div>
                  )}
                </div>
              </div>

              {!newAppointment.client_id && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                    <input
                      type="text"
                      value={newAppointment.client_name}
                      onChange={(e) => setNewAppointment({ ...newAppointment, client_name: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-900 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={newAppointment.client_email}
                      onChange={(e) => setNewAppointment({ ...newAppointment, client_email: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-900 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                    <input
                      type="tel"
                      value={newAppointment.client_phone}
                      onChange={(e) => setNewAppointment({ ...newAppointment, client_phone: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-900 focus:outline-none"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Start Time *</label>
                  <input
                    type="datetime-local"
                    required
                    value={newAppointment.start_time}
                    onChange={(e) => setNewAppointment({ ...newAppointment, start_time: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-900 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">End Time *</label>
                  <input
                    type="datetime-local"
                    required
                    value={newAppointment.end_time}
                    onChange={(e) => setNewAppointment({ ...newAppointment, end_time: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-900 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Location</label>
                  <input
                    type="text"
                    value={newAppointment.location}
                    onChange={(e) => setNewAppointment({ ...newAppointment, location: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-900 focus:outline-none"
                    placeholder="e.g., Store, Client Office, Virtual"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
                  <input
                    type="text"
                    value={newAppointment.address}
                    onChange={(e) => setNewAppointment({ ...newAppointment, address: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-900 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                <textarea
                  value={newAppointment.description}
                  onChange={(e) => setNewAppointment({ ...newAppointment, description: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-900 focus:outline-none"
                  rows="3"
                  placeholder="Details about this appointment..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Internal Notes</label>
                <textarea
                  value={newAppointment.notes}
                  onChange={(e) => setNewAppointment({ ...newAppointment, notes: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-slate-900 focus:outline-none"
                  rows="2"
                  placeholder="Private notes (not visible to client)..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-2xl font-semibold hover:from-slate-950 hover:to-slate-800 transition-all"
                >
                  Create Appointment
                </button>
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-2xl font-semibold hover:bg-gray-300 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Appointment Detail Modal */}
      {showDetailModal && selectedAppointment && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setShowDetailModal(false);
            setSelectedAppointment(null);
          }}
        >
          <div
            className="bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">{selectedAppointment.title}</h2>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${getTypeColor(selectedAppointment.appointment_type)}`}>
                    {selectedAppointment.appointment_type}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(selectedAppointment.status)}`}>
                    {selectedAppointment.status}
                  </span>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedAppointment(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Time */}
              <div className="bg-blue-50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-blue-600 font-semibold mb-1">
                  <Clock className="w-5 h-5" />
                  <span>Date & Time</span>
                </div>
                <div className="text-gray-900 font-semibold">
                  {formatDateTime(selectedAppointment.start_time)}
                </div>
                {selectedAppointment.end_time && (
                  <div className="text-sm text-gray-600">
                    to {formatDateTime(selectedAppointment.end_time)}
                  </div>
                )}
              </div>

              {/* Client Info */}
              {(selectedAppointment.client_name || selectedAppointment.client_id) && (
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-slate-600 font-semibold mb-2">
                    <User className="w-5 h-5" />
                    <span>Client</span>
                  </div>
                  <div className="space-y-1">
                    <div className="font-semibold text-gray-900">
                      {selectedAppointment.client_name || `Client #${selectedAppointment.client_id}`}
                    </div>
                    {selectedAppointment.client_email && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4" />
                        {selectedAppointment.client_email}
                      </div>
                    )}
                    {selectedAppointment.client_phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="w-4 h-4" />
                        {selectedAppointment.client_phone}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Location */}
              {selectedAppointment.location && (
                <div className="bg-green-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-green-600 font-semibold mb-1">
                    <MapPin className="w-5 h-5" />
                    <span>Location</span>
                  </div>
                  <div className="text-gray-900">{selectedAppointment.location}</div>
                  {selectedAppointment.address && (
                    <div className="text-sm text-gray-600 mt-1">{selectedAppointment.address}</div>
                  )}
                </div>
              )}

              {/* Description */}
              {selectedAppointment.description && (
                <div className="border-2 border-gray-200 rounded-xl p-4">
                  <div className="font-semibold text-gray-700 mb-2">Description</div>
                  <div className="text-gray-600">{selectedAppointment.description}</div>
                </div>
              )}

              {/* Notes */}
              {selectedAppointment.notes && (
                <div className="border-2 border-gray-200 rounded-xl p-4">
                  <div className="font-semibold text-gray-700 mb-2">Notes</div>
                  <div className="text-gray-600">{selectedAppointment.notes}</div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4 border-t-2 border-gray-200">
                <button
                  onClick={() => downloadCalendarInvite(selectedAppointment)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-xl transition-all"
                >
                  <Download className="w-4 h-4" />
                  Download .ics
                </button>

                {selectedAppointment.status === 'Scheduled' && (
                  <button
                    onClick={() => {
                      handleUpdateStatus(selectedAppointment.id, 'Confirmed');
                      setShowDetailModal(false);
                      setSelectedAppointment(null);
                    }}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-green-700 bg-green-100 hover:bg-green-200 rounded-xl transition-all"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Confirm
                  </button>
                )}

                <button
                  onClick={() => {
                    handleDelete(selectedAppointment.id);
                    setShowDetailModal(false);
                    setSelectedAppointment(null);
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-red-700 bg-red-100 hover:bg-red-200 rounded-xl transition-all ml-auto"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Day Detail Modal - Apple Calendar Style */}
      {showDayModal && selectedDay && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setShowDayModal(false);
            setSelectedDay(null);
          }}
        >
          <div
            className="bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-bold text-gray-900">
                {selectedDay.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </h2>
              <button
                onClick={() => {
                  setShowDayModal(false);
                  setSelectedDay(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {getAppointmentsForDay(selectedDay).length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <CalendarIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-semibold mb-2">No appointments scheduled</p>
                <p className="text-sm mb-6">Click below to add an appointment for this day</p>
                <button
                  onClick={() => {
                    setShowDayModal(false);
                    setShowCreateModal(true);
                    // Pre-fill the date in the create form
                    const dateStr = selectedDay.toISOString().slice(0, 16);
                    setNewAppointment({
                      ...newAppointment,
                      start_time: dateStr,
                      end_time: dateStr
                    });
                  }}
                  className="px-6 py-3 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-all"
                >
                  <Plus className="w-4 h-4 inline mr-2" />
                  New Appointment
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {getAppointmentsForDay(selectedDay).map((apt) => (
                  <div
                    key={apt.id}
                    className="border-2 border-gray-200 rounded-2xl p-5 hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer"
                    onClick={() => {
                      setShowDayModal(false);
                      setSelectedAppointment(apt);
                      setShowDetailModal(true);
                    }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-gray-900">{apt.title}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${getTypeColor(apt.appointment_type)}`}>
                            {apt.appointment_type}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-blue-600 font-semibold">
                          <Clock className="w-4 h-4" />
                          {new Date(apt.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                          {apt.end_time && (
                            <span> - {new Date(apt.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</span>
                          )}
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(apt.status)}`}>
                        {apt.status}
                      </span>
                    </div>

                    {apt.client_name && (
                      <div className="flex items-center gap-2 text-gray-700 mb-1">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{apt.client_name}</span>
                      </div>
                    )}

                    {apt.location && (
                      <div className="flex items-center gap-2 text-gray-700">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{apt.location}</span>
                      </div>
                    )}

                    {apt.description && (
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">{apt.description}</p>
                    )}
                  </div>
                ))}

                <button
                  onClick={() => {
                    setShowDayModal(false);
                    setShowCreateModal(true);
                    const dateStr = selectedDay.toISOString().slice(0, 16);
                    setNewAppointment({
                      ...newAppointment,
                      start_time: dateStr,
                      end_time: dateStr
                    });
                  }}
                  className="w-full py-3 border-2 border-dashed border-gray-300 text-gray-600 rounded-xl font-semibold hover:border-slate-400 hover:text-slate-700 transition-all"
                >
                  <Plus className="w-4 h-4 inline mr-2" />
                  Add Another Appointment
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentsPage;
